import { datadogRum } from '@datadog/browser-rum';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { ehErroHttp, temCodigoHttp } from '@/shared/utils/erroHttp';
import { reportError } from '@/shared/utils/reportError';

import type {
  ResultadoDoSorteioDoSubstituto,
  SorteioDoSubstitutoNaoFeito,
} from '../../types/sortearSubstituto';
import { motivoAcaoLegivel } from '../../utils/motivoLoteLabels';
import { sortearSubstitutoService } from './services';

/** O 409 da corrida: a vaga já ganhou substituto por outro clique ou outro caminho. */
const CODIGO_VAGA_JA_PREENCHIDA = 'VAGA_JA_PREENCHIDA';

const MENSAGEM_PADRAO = 'Erro ao sortear o árbitro substituto';

function naoFeitoPorErro(error: unknown, processoId: string): SorteioDoSubstitutoNaoFeito {
  if (temCodigoHttp(error, CODIGO_VAGA_JA_PREENCHIDA)) {
    datadogRum.addAction('sortear_substituto', { resultado: 'VAGA_JA_PREENCHIDA' });
    return { tipo: 'vaga_ja_preenchida' };
  }
  if (ehErroHttp(error) && error.status < 500) {
    datadogRum.addAction('sortear_substituto', {
      resultado: 'RECUSADO',
      statusHttp: error.status,
      codigo: error.codigo,
    });
  } else {
    reportError(error instanceof Error ? error : new Error(motivoAcaoLegivel(error)), {
      source: 'sortear-substituto',
      processoArbitralId: processoId,
      statusHttp: ehErroHttp(error) ? error.status : null,
    });
  }
  return { tipo: 'erro', mensagem: motivoAcaoLegivel(error, MENSAGEM_PADRAO) };
}

/**
 * Sorteio do substituto (C2/C3). Só o sorteio feito é `fulfilled`, porque o `fulfilled` vira o
 * evento `secretaria_substituto_sorteado` no PostHog. A corrida perdida (409) e a recusa de
 * negócio (4xx, ex.: ninguém elegível) saem no `rejected` e não viram erro no RUM — a recusa é a
 * frase que o diálogo mostra —; só 5xx e falha de rede. A telemetria leva o desfecho, nunca o nome.
 */
export const sortearSubstituto = createAsyncThunk<
  ResultadoDoSorteioDoSubstituto,
  string,
  { rejectValue: SorteioDoSubstitutoNaoFeito }
>('secretariaArbitralSortearSubstituto/sortear', async (processoId, { rejectWithValue }) => {
  try {
    const resultado = await sortearSubstitutoService.sortear(processoId);
    datadogRum.addAction('sortear_substituto', { resultado: 'SORTEADO' });
    return resultado;
  } catch (error: unknown) {
    return rejectWithValue(naoFeitoPorErro(error, processoId));
  }
});
